import type { FC, PropsWithChildren } from "react";
import { createContext, useContext, useState } from "react";

import { useSharedPushUtils } from "~/features/push-notifications/use-shared-push-utils";
import {
  fetchPushSubscription,
  getCurrentSubscription,
} from "~/features/push-notifications/utils";
import { useHydrated } from "~/hooks/use-hydrated";
import { usePushTracing } from "~/hooks/use-push-tracing";

import { useQuery } from "@tanstack/react-query";

type SubscriptionState =
  | "init"
  | "subscribed"
  | "deferred"
  | "unsupported"
  | NotificationPermission;

const PushSubscriptionContext = createContext<{
  status: SubscriptionState;
  defer(): void;
} | null>(null);

const getNotificationPermission = () =>
  typeof Notification === "undefined" ? "unsupported" : Notification.permission;

export const PushSubscriptionProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [isDeferred, setIsDeferred] = useState(false);
  const { queryKey } = useSharedPushUtils();
  const isHydrated = useHydrated();

  usePushTracing();

  const browserSub = useQuery({
    enabled: isHydrated,
    queryKey: [...queryKey, "browser"] as const,
    staleTime: 0,
    initialData: "unsupported",
    networkMode: "always",
    async queryFn() {
      const perm = getNotificationPermission();
      if (perm === "granted") {
        const sub = await getCurrentSubscription();
        if (sub) {
          return sub;
        }
      }

      return perm;
    },
  });

  const status = useQuery({
    queryKey: [...queryKey, "status", browserSub.data] as const,
    enabled: browserSub.isFetched,
    networkMode: "offlineFirst",
    async queryFn({
      queryKey: [, employeeId, , sub],
    }): Promise<SubscriptionState> {
      if (!employeeId) {
        return "init";
      }

      if (typeof sub === "string") {
        return sub;
      }

      if (sub) {
        const inDb = await fetchPushSubscription(
          {
            action: "read",
            subscription: sub,
            employeeId,
          },
          "POST",
        );

        if (inDb.ok) {
          return "subscribed";
        }
      }

      return getNotificationPermission();
    },
  });

  return (
    <PushSubscriptionContext.Provider
      value={{
        status:
          isDeferred && status.data === "default"
            ? "deferred"
            : status.data || "init",
        defer: () => setIsDeferred(true),
      }}
    >
      {children}
    </PushSubscriptionContext.Provider>
  );
};

export const usePushSubscriptionState = () => {
  const context = useContext(PushSubscriptionContext);

  if (!context) {
    throw new Error(
      "usePushSubscriptionState used outside of PushSubscriptionProvider",
    );
  }

  return context;
};
